import { basicToaster } from '@ui/components/Toast';
import { Text } from '@ui/lib/park/ui/text';
import { GenericToastOptions } from '@ui/types/tx';
import { getExplorerLink } from '@ui/utils/links';
import { Trans, useTranslation } from 'next-i18next';
import { useRef } from 'react';

import { getChainId } from './datachain/useDataChainStore';

export const useMutationToast = () => {
  const loadingToast = useRef<string>();

  const defaultSuccessToastOpts = (txHash: string) => ({
    title: 'Completed',
    description: 'Operation completed successfully!',
    action: {
      label: 'View on Monad Explorer',
      onClick: () => {
        window.open(
          getExplorerLink(txHash, 'transaction', getChainId()),
          '_blank',
        );
      },
    },
  });

  const defaultErrorToastOpts = (errorMsg?: string) => ({
    title: 'Operation Failed',
    description: errorMsg ? (
      <Trans
        i18nKey={`<T1>An unexpected error occurred, please try again.</T1> <T2>Error details: ${errorMsg}</T2>`}
        values={{
          errorMsg: errorMsg,
        }}
        components={{
          T1: <Text />,
          T2: <Text mt={2} />,
        }}
      />
    ) : (
      'Something went wrong, please try again'
    ),
  });

  const defaultOnMutateToastOpts = {
    title: 'In Progress',
    description: 'Please wait while we process your request',
  };

  return {
    onMutate: (onMutateToastOpts?: GenericToastOptions) => {
      loadingToast.current = basicToaster.loading({
        ...defaultOnMutateToastOpts,
        ...onMutateToastOpts,
      });
    },
    onSuccess: (onSuccessToastOpts?: GenericToastOptions) => {
      basicToaster.success({
        ...defaultSuccessToastOpts(onSuccessToastOpts?.txHash || ''),
        ...onSuccessToastOpts,
      });
    },
    onError: ({
      errorMsg,
      onErrorToastOpts,
    }: {
      errorMsg?: string;
      onErrorToastOpts?: GenericToastOptions;
    }) => {
      basicToaster.error({
        ...defaultErrorToastOpts(errorMsg),
        ...onErrorToastOpts,
      });
    },
    onSettled: () => {
      basicToaster.dismiss(loadingToast.current);
      loadingToast.current = '';
    },
  };
};
